/**
 * FormField — molecule (ADR-0007). Label + control slot + error/helper text + a11y wiring.
 */
import { type ReactNode } from 'react'
import { Icon } from '@/components/icons/Icon'
import { Label } from '@/components/ui/Label'

export interface FormFieldProps {
  id: string
  label: string
  required?: boolean
  error?: string
  helper?: ReactNode
  children: (a: { id: string; describedBy: string | undefined; hasError: boolean }) => ReactNode
}

export function FormField({ id, label, required, error, helper, children }: FormFieldProps) {
  const hasError = !!error
  const errorId = `${id}-error`
  const helperId = `${id}-helper`
  const describedBy = hasError ? errorId : helper ? helperId : undefined

  return (
    <div>
      <Label htmlFor={id} required={required}>{label}</Label>
      {children({ id, describedBy, hasError })}
      {hasError && (
        <p id={errorId} role="alert" className="flex items-center gap-1 text-xs text-text-danger font-medium mt-1">
          <Icon name="alert-circle" size={14} aria-hidden="true" />
          {error}
        </p>
      )}
      {!hasError && helper && (
        <p id={helperId} className="text-xs text-text-secondary mt-1">{helper}</p>
      )}
    </div>
  )
}
